import React, { useState } from 'react';

import { API_DATE_FORMAT } from '@cd3p/core/constants/common';
import { DocumentFields } from '@cd3p/core/modules/documents';
import { APIDocument } from '@cd3p/core/types/api';
import { textField } from '@cd3p/core/utils/fields';
import { DateField, FormField, Modal } from 'components';
import { useHandleApiResult } from 'hooks/useHandleApiResult';
import { FieldError, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormProvider, dayjs } from 'third-party';

export type FormProps = {
  [DocumentFields.Id]: string;
  [DocumentFields.Title]: string | null;
  [DocumentFields.IsCategory]: boolean;
  [DocumentFields.Position]: number;
  [DocumentFields.SectionId]: string;
  [DocumentFields.ParentId]: string | null;
  [DocumentFields.Text]: string | null;
  [DocumentFields.PublishedDate]: string;
};

const defaultValues = {
  [DocumentFields.Title]: '',
  [DocumentFields.Text]: '',
  [DocumentFields.ParentId]: null,
  [DocumentFields.IsCategory]: false,
  [DocumentFields.Position]: 0,
  [DocumentFields.PublishedDate]: dayjs().format(API_DATE_FORMAT),
};

type DocsSectionsTreeModalProps = {
  open?: boolean;
  header: string;
  confirmLabel: string;
  onClose: () => void;
  onSuccess?: (document: APIDocument) => void;
  saveDocument?: (data: Partial<APIDocument>) => void;
  document: Partial<APIDocument>;
  hiddenFields?: DocumentFields[];
};

export const DocsSectionsTreeModal: React.FC<DocsSectionsTreeModalProps> = ({
  open = false,
  header,
  confirmLabel,
  saveDocument,
  onClose,
  onSuccess,
  document,
  hiddenFields,
}) => {
  const { t } = useTranslation();
  const formMethods = useForm<FormProps>({
    mode: 'onChange',
    defaultValues: {
      ...defaultValues,
      ...document,
    } as APIDocument,
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = formMethods;

  const handleApiResult = useHandleApiResult();

  const [isPending, setIsPending] = useState(false);
  const onSubmit = async (data: FormProps) => {
    if (!isValid) {
      return;
    }
    setIsPending(true);
    handleApiResult<APIDocument>(
      () => saveDocument?.(data as unknown as Partial<APIDocument>),
      ({ result }) => {
        onSuccess?.(result.payload);
        setIsPending(false);
        onClose();
      },
      () => {
        setIsPending(false);
        onClose();
      },
    );
  };

  return (
    <Modal
      open={open}
      header={header}
      confirmLabel={confirmLabel}
      onConfirm={() => {
        handleSubmit(onSubmit)();
      }}
      isDisabled={isPending}
      isPending={isPending}
      isConfirmDisabled={!isValid}
      onClose={onClose}
    >
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          {!hiddenFields?.includes(DocumentFields.Title) && (
            <FormField
              isRequired
              fieldName={DocumentFields.Title}
              label={t('docs.modal.nameLabel')}
              fieldError={errors[DocumentFields.Title] as FieldError}
              requiredErrorMessage={t('common.form.error.emptyField')}
              placeholder={t('docs.modal.namePlaceholder')}
              control={control}
              isDisabled={isPending}
              maxLength={textField.maxLength}
            />
          )}
          {!hiddenFields?.includes(DocumentFields.PublishedDate) && (
            <DateField
              isRequired
              showError
              fieldName={DocumentFields.PublishedDate}
              label={t('docs.modal.publishedDateLabel')}
              fieldError={errors[DocumentFields.PublishedDate] as FieldError}
              requiredErrorMessage={t('common.form.error.emptyField')}
              placeholder={t('docs.modal.publishedDatePlaceholder')}
              control={control}
              isDisabled={isPending}
            />
          )}
        </form>
      </FormProvider>
    </Modal>
  );
};
